import React from 'react'
import { useIntl } from 'react-intl'
import { Grid, ListItemText } from '@mui/material'
import { withStyles } from '@mui/styles'

import { gridRowStyles } from './grid-row.styles'

/**
 * @typedef {object} GridRowItem
 * @property {string} label - ID de traducción para la etiqueta del item (se usa con react-intl).
 * @property {string} [value] - La clave del campo en `data` que se mostrará como valor secundario.
 * @property {function(object, object): string} [valueParsed] - Función personalizada para procesar el valor. Recibe `data` e `intl` como parámetros.
 * @property {boolean} [multiline] - Si es `true`, el texto se muestra con saltos de línea preservados.
 */

/**
 * @typedef {object} GridRowComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {GridRowItem[]} items - Array de items a mostrar en la fila del grid.
 * @property {object} data - Objeto con los datos que se mostrarán en los items.
 * @property {number} [spacing=4] - Espaciado entre items del Grid de Material-UI.
 * @property {'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'} [justify='space-between'] - Alineación horizontal de los items en el Grid.
 */

/**
 * Un componente que renderiza una fila de datos en formato Grid con etiquetas y valores.
 * 
 * Este componente utiliza Material-UI Grid y ListItemText para mostrar pares de etiqueta-valor
 * de forma organizada y responsive. Características principales:
 * 
 * - **Internacionalización**: Las etiquetas se traducen automáticamente usando react-intl
 * - **Valores personalizados**: Soporte para funciones de parseo de valores
 * - **Texto multilínea**: Opción para preservar saltos de línea en los valores
 * - **Layout flexible**: Control total sobre espaciado y alineación
 * 
 * Ideal para mostrar información estructurada como detalles de entidades, resúmenes de datos, etc.
 * 
 * @component
 * @example
 * const MyDataDisplay = () => {
 *   const userData = {
 *     nombre: 'Juan Pérez',
 *     email: 'juan@example.com',
 *     fecha_registro: '2024-01-15T10:30:00Z',
 *     activo: true,
 *   };
 * 
 *   const items = [
 *     { label: 'user.name', value: 'nombre' },
 *     { label: 'user.email', value: 'email' },
 *     {
 *       label: 'user.registrationDate',
 *       valueParsed: (data, intl) => intl.formatDate(new Date(data.fecha_registro)),
 *     },
 *     {
 *       label: 'user.status',
 *       valueParsed: (data) => data.activo ? 'Activo' : 'Inactivo',
 *     },
 *   ];
 * 
 *   return (
 *     <GridRowComponent
 *       items={items}
 *       data={userData}
 *       spacing={3}
 *       justify="flex-start"
 *     />
 *   );
 * };
 * 
 * @param {GridRowComponentProps} props - Las props del componente GridRowComponent.
 */
export const GridRowComponent = withStyles(gridRowStyles)(({
  classes,
  items,
  data,
  spacing = 4,
  justify = 'space-between',
}) => {
  const intl = useIntl()

  return (
    <Grid container spacing={spacing} justifyContent={justify}>
      {items.map((item, i) => {
        let multiline = item.multiline ? { whiteSpace: 'pre-line' } : {}
        return (
          <Grid item key={i}>
            <ListItemText
              primary={intl.formatMessage({ id: item.label })}
              secondary={item.valueParsed ? item.valueParsed(data, intl) : data[item.value]}
              primaryTypographyProps={{ variant: 'subtitle2' }}
              secondaryTypographyProps={{ variant: 'body2' }}
              style={multiline}
            ></ListItemText>
          </Grid>
        )
      })}
    </Grid>

    //   <div className={classes.container}>
    //     {items.map((item, i) => (
    //       <div key={i} name={i} className={classes.item}>
    //         <Typography variant="subtitle2">{item.label}</Typography>
    //         <Typography variant="body2">
    //           {item.valueParsed ? item.valueParsed(data) : data[item.value]}
    //         </Typography>
    //       </div>
    //     ))}
    //   </div>
  )
})
